import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavBar, Toast, Dialog, Skeleton, Button, Sticky, Tag, Typography } from 'react-vant';
import { ArrowLeft, Success, Cross, ClockO, Description, Passed } from '@react-vant/icons';
import { getSessionDetail, getRegistrations, takeAttendance, getAttendanceHistory } from '../../../api/club';
import dayjs from 'dayjs';

const ClubAttendance = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({}); // { studentId: status }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [sessionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionRes, studentsRes, historyRes] = await Promise.all([
        getSessionDetail(sessionId),
        getRegistrations({ session_id: sessionId, status: 'confirmed', page_size: 1000 }),
        getAttendanceHistory(sessionId)
      ]);

      setSession(sessionRes);
      const list = studentsRes.items || [];
      setStudents(list);

      // 初始化考勤数据
      const initialData = {};
      list.forEach(s => {
        initialData[s.student_id] = 'present';
      });

      // 检查今日是否有记录
      const today = dayjs().format('YYYY-MM-DD');
      const todayRecord = historyRes.find(h => dayjs(h.attendance_date).format('YYYY-MM-DD') === today);
      if (todayRecord && todayRecord.details) {
        todayRecord.details.forEach(d => {
          initialData[d.student_id] = d.status;
        });
      }

      setAttendanceData(initialData);
    } catch (error) {
      Toast.fail('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'present': return { label: '出勤', color: '#00b578', icon: <Success /> };
      case 'absent': return { label: '缺勤', color: '#e11d48', icon: <Cross /> };
      case 'late': return { label: '迟到', color: '#d97706', icon: <ClockO /> };
      case 'excused': return { label: '请假', color: '#056b4f', icon: <Description /> };
      default: return { label: '未知', color: '#94a3b8' };
    }
  };

  const toggleStatus = (studentId) => {
    const statuses = ['present', 'absent', 'late', 'excused'];
    const currentStatus = attendanceData[studentId] || 'present';
    const nextIndex = (statuses.indexOf(currentStatus) + 1) % statuses.length;
    setAttendanceData({
      ...attendanceData,
      [studentId]: statuses[nextIndex]
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const details = students.map(s => ({
        student_id: s.student_id,
        status: attendanceData[s.student_id] || 'present'
      }));

      await takeAttendance({
        session_id: parseInt(sessionId),
        attendance_date: dayjs().format('YYYY-MM-DD'),
        details
      });

      Toast.success('点名成功');
      setTimeout(() => navigate(-1), 1500);
    } catch (error) {
      Toast.fail(error.response?.data?.detail || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !session) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <NavBar title="课堂点名" leftText={<ArrowLeft size={20} />} onClickLeft={() => navigate(-1)} fixed placeholder />
        <div style={{ padding: '20px' }}><Skeleton title row={10} /></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '80px' }}>
      <Sticky>
        <NavBar 
          title={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: 800 }}>{session.club_name}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>今日点名 ({dayjs().format('YYYY-MM-DD')})</div>
            </div>
          } 
          leftText={<ArrowLeft size={20} />} 
          onClickLeft={() => navigate(-1)}
          rightText={<ClockO size={20} color="#056b4f" />}
          onClickRight={() => navigate(`/clubs/teacher/attendance/history/${sessionId}`)}
          border={false}
          style={{ background: '#fff' }}
        />
      </Sticky>

      <div style={{ padding: '12px 16px', background: '#f0fdf4', color: '#056b4f', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Description size={14} /> 提示：点击学生状态图标可循环切换 (出勤/缺勤/迟到/请假)
      </div>

      <div style={{ padding: '12px' }}>
        {students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>该社团目前没有正式报名的学生</div>
        ) : (
          students.map(student => {
            const status = attendanceData[student.student_id] || 'present';
            const config = getStatusConfig(status);
            return (
              <div 
                key={student.id}
                onClick={() => toggleStatus(student.student_id)}
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}
              >
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                    {student.student_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {student.grade} {student.class_name}
                  </div>
                </div>
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px', 
                    color: config.color,
                    border: `1px solid ${config.color}`,
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: 600
                  }}
                >
                  {config.icon}
                  {config.label}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '12px 16px', background: '#fff', boxShadow: '0 -2px 10px rgba(0,0,0,0.05)', display: 'flex', gap: '12px' }}>
        <Button 
          type="primary" 
          block 
          round 
          loading={submitting} 
          onClick={handleSubmit}
          style={{ background: '#056b4f', borderColor: '#056b4f' }}
        >
          确认并提交今日点名
        </Button>
      </div>
    </div>
  );
};

export default ClubAttendance;
